/**
 * Die Pruefliste: der A+S-Plan gegen die bekannten Maengel.
 *
 * Quelle: BG BAU, Bauportal 1/2021, „Herausforderungen des Arbeits- und
 * Sicherheitsplans bei Arbeiten in kontaminierten Bereichen" — eine
 * Aufarbeitung dessen, was in der Praxis regelmaessig fehlt, gegliedert in
 * Pruefpunkte. Die Nummerierung („Check 1" … „Check 17") ist von dort
 * uebernommen, damit ein Anwender die Quelle wiederfindet.
 *
 * ═══ WARUM DAS DAZUGEHOERT ════════════════════════════════════════════════
 * Ein Formular sammelt Angaben. Ein Werkzeug haelt dagegen, was
 * erfahrungsgemaess fehlt. Der Unterschied zeigt sich genau an den Stellen,
 * die man beim Ausfuellen nicht vermisst: Die Historische Erkundung fehlt
 * nicht auffaellig — es ist einfach kein Feld leer. Die taetigkeitsbezogene
 * Beurteilung fehlt nicht auffaellig — der Plan springt direkt vom
 * Stoffspektrum zu den Schutzmassnahmen und liest sich fluessig.
 *
 * Deshalb steht die Liste hier, und deshalb hat jeder Punkt ein `warum`: der
 * Bearbeiter soll nicht abhaken, sondern verstehen, wogegen geprueft wird.
 *
 * ═══ AUTOMATISCH ODER VON HAND ════════════════════════════════════════════
 * `automatisch` sagt, ob `asplanLogic.ts` den Punkt selbst beurteilen kann.
 * Wo das geht, ist die Handeingabe ueberfluessig; wo nicht (weil es um
 * fachliche Angemessenheit geht, nicht um Vorhandensein), bleibt es beim
 * Urteil des Bearbeiters. Ein Automat, der Angemessenheit behauptet, waere
 * schlimmer als keiner.
 */

export const PRUEFLISTE_KATALOG_VERSION = '2026-08-22';

export interface PruefPunkt {
    key: string;
    /** Nummer aus der Quelle, fuer die Wiederauffindbarkeit. */
    nummer: string;
    bereich: 'erkundung' | 'analytik' | 'stoffe' | 'arbeitsbereiche' | 'beurteilung'
        | 'schutzmassnahmen' | 'psa' | 'messung';
    frage: string;
    /** Der Mangel, gegen den geprueft wird — woertlich am Befund der BG BAU. */
    warum: string;
    /** Kapitel des Plans, das die Antwort traegt. */
    kapitel: string;
    automatisch: boolean;
}

export const PRUEFLISTE: PruefPunkt[] = [
    // ── Historische Erkundung ──────────────────────────────────────────────
    {
        key: 'check_1',
        nummer: 'Check 1',
        bereich: 'erkundung',
        frage: 'Liegt eine Historische Erkundung vor, und sind die analytisch untersuchten '
            + 'Stoffe daraus abgeleitet?',
        warum: 'Fehlt die Historische Erkundung, ist eine gezielte Auswahl der zu '
            + 'untersuchenden Stoffe nicht möglich — die Analytik trifft dann '
            + 'möglicherweise am tatsächlichen Stoffspektrum vorbei.',
        kapitel: 'Historische Erkundung',
        automatisch: true,
    },
    {
        key: 'check_2',
        nummer: 'Check 1b',
        bereich: 'erkundung',
        frage: 'Sind Gebäudeschadstoffe berücksichtigt (PCB, PAK, Holzschutzmittel)?',
        warum: 'Bei Bauwerken im Bestand werden regelmäßig nur die Bodenbelastungen '
            + 'betrachtet und die im Gebäude gebundenen Schadstoffe vergessen.',
        kapitel: 'Historische Erkundung',
        automatisch: true,
    },
    {
        key: 'check_3',
        nummer: 'Check 1c',
        bereich: 'erkundung',
        frage: 'Sind Bauwerke im Untergrund, Tanks, Leitungen und Kampfmittel abgefragt?',
        warum: 'Schächte, stillgelegte Tanks und Leitungen sind sowohl Schadstoffquelle als '
            + 'auch Unfallgefahr; der Kampfmitteldienst ist gesondert zu beteiligen.',
        kapitel: 'Historische Erkundung',
        automatisch: true,
    },

    // ── Analytik ───────────────────────────────────────────────────────────
    {
        key: 'check_4',
        nummer: 'Check 2',
        bereich: 'analytik',
        frage: 'Stammen die Analysen aus einer Untersuchung für den ARBEITSSCHUTZ — oder '
            + 'nur aus der abfallrechtlichen Deklaration?',
        warum: 'Abfallrechtliche Beurteilungen („LAGA“) haben für den Arbeitsschutz nur '
            + 'eingeschränkte Aussagekraft: Mischproben verdünnen hohe Einzelgehalte, und '
            + 'die Aufnahmewege der Beschäftigten spielen dort keine Rolle.',
        kapitel: 'Gefahrstoffermittlung',
        automatisch: false,
    },
    {
        key: 'check_5',
        nummer: 'Check 3',
        bereich: 'analytik',
        frage: 'Sind Summenparameter (BTEX, MKW, „Cyanid gesamt“, „Chrom“) in die '
            + 'arbeitsschutzrelevanten Einzelstoffe aufgelöst?',
        warum: 'Eine Sammelangabe trennt nicht zwischen flüchtig und gebunden, zwischen '
            + 'Cr(III) und Cr(VI), zwischen komplexem und leicht freisetzbarem Cyanid. Genau '
            + 'diese Unterscheidung bestimmt aber Filterwahl und Messkonzept.',
        kapitel: 'Gefahrstoffermittlung',
        automatisch: false,
    },
    {
        key: 'check_6',
        nummer: 'Check 4',
        bereich: 'analytik',
        frage: 'Ist die räumliche Verteilung der Belastung dokumentiert (Kataster, Lageplan, '
            + 'Tiefenlage)?',
        warum: 'Ohne Verortung lässt sich kein Arbeitsbereich abgrenzen — und ohne '
            + 'Tiefenangabe nicht sagen, ab wann Weißbereich gilt.',
        kapitel: 'Rasterfelder und Proben',
        automatisch: true,
    },

    // ── Stoffeigenschaften ─────────────────────────────────────────────────
    {
        key: 'check_9',
        nummer: 'Check 9',
        bereich: 'stoffe',
        frage: 'Ist begründet, WELCHE Stoffe in den Plan aufgenommen wurden — und welche '
            + 'nicht?',
        warum: 'Werden alle Stoffe beschrieben, die in irgendeiner Konzentration im Labor '
            + 'auftauchen, folgen daraus unnötige Schutzmaßnahmen — und die notwendigen '
            + 'gehen darin unter.',
        kapitel: 'Gefahrstoffe',
        automatisch: true,
    },
    {
        key: 'check_10',
        nummer: 'Check 10',
        bereich: 'stoffe',
        frage: 'Sind Erscheinungsform und Mobilität projektbezogen beschrieben (Oel, Staub, '
            + 'Dampf; flüchtig, staubgängig, hautresorptiv)?',
        warum: 'Ob PAK als Oel, als Staub oder als Dampf vorliegt, entscheidet über die '
            + 'Schutzmaßnahme. Eine allgemeine Stoffbeschreibung aus dem Lehrbuch '
            + 'beantwortet das nicht.',
        kapitel: 'Gefahrstoffe',
        automatisch: true,
    },
    {
        key: 'check_11',
        nummer: 'Check 11',
        bereich: 'stoffe',
        frage: 'Sind die Stoffdaten aus einer belastbaren Quelle übernommen (z. B. GESTIS) '
            + 'und mit Fassung vermerkt?',
        warum: 'Grenzwerte werden fortgeschrieben. Ohne Quelle und Fassung lässt sich '
            + 'später nicht mehr sagen, welcher Maßstab bei Freigabe galt.',
        kapitel: 'Gefahrstoffe',
        automatisch: true,
    },

    // ── Arbeitsbereiche und Taetigkeiten ───────────────────────────────────
    {
        key: 'check_12',
        nummer: 'Check 12',
        bereich: 'arbeitsbereiche',
        frage: 'Ist die Baustelle in Arbeitsbereiche mit potenzieller Exposition unterteilt?',
        warum: 'Ohne Unterteilung gilt am Ende überall dieselbe — meist zu hohe — '
            + 'Schutzstufe, und die wird dann nicht eingehalten.',
        kapitel: 'Arbeitsbereiche',
        automatisch: true,
    },
    {
        key: 'check_13',
        nummer: 'Check 13',
        bereich: 'arbeitsbereiche',
        frage: 'Sind alle Arbeitsschritte, Arbeitsverfahren und Einzeltätigkeiten mit '
            + 'Expositionspotenzial erfasst — „Wer macht wann was wie, wie oft, wie lange?“',
        warum: 'Vorgaben wie „Aushub kontaminierten Bodens“ sind zu allgemein. Die Wahl des '
            + 'Arbeitsverfahrens bestimmt die Gefährdung, nicht der Stoff allein.',
        kapitel: 'Tätigkeiten',
        automatisch: true,
    },
    {
        key: 'check_13b',
        nummer: 'Check 13b',
        bereich: 'arbeitsbereiche',
        frage: 'Sind die Nebenarbeiten einbezogen — Transport, Bereitstellungslager, '
            + 'Wasserhaltung, Abfallaufbereitung, Vermessung, Reinigung?',
        warum: 'Sie werden regelmäßig übersehen, weil sie nicht nach „Sanierung“ aussehen. '
            + 'Exponiert sind die Beschäftigten dort trotzdem.',
        kapitel: 'Arbeitsbereiche',
        automatisch: true,
    },
    {
        key: 'check_13c',
        nummer: 'Check 13c',
        bereich: 'arbeitsbereiche',
        frage: 'Sind die Umgebungsbedingungen je Arbeitsbereich beschrieben (Temperatur, '
            + 'Enge, Belüftung, Witterung)?',
        warum: 'Klimatische Bedingungen werden vernachlässigt — sie gehen aber unmittelbar '
            + 'in die Tragezeitbegrenzung und damit in die Bauzeit ein.',
        kapitel: 'Arbeitsbereiche',
        automatisch: true,
    },

    // ── Gefaehrdungsbeurteilung ────────────────────────────────────────────
    {
        key: 'check_gb1',
        nummer: 'Check GB1',
        bereich: 'beurteilung',
        frage: 'Ist je Tätigkeit geprüft, OB überhaupt eine Exposition besteht — und über '
            + 'welchen Weg (inhalativ, dermal, oral)?',
        warum: 'Der häufigste Mangel überhaupt: der direkte Sprung vom Stoffspektrum zu den '
            + 'Schutzmaßnahmen, ohne tätigkeitsbezogene Analyse. Zwei Personen in derselben '
            + 'Arbeit können verschiedene Aufnahmewege haben.',
        kapitel: 'Tätigkeiten',
        automatisch: true,
    },
    {
        key: 'check_gb2',
        nummer: 'Check GB2',
        bereich: 'beurteilung',
        frage: 'Sind Dauer und Häufigkeit der Exposition je Tätigkeit angegeben?',
        warum: 'Ohne Dauer keine Expositionsabschätzung — und ohne sie keine begründete '
            + 'Schutzklasse.',
        kapitel: 'Tätigkeiten',
        automatisch: true,
    },

    // ── Schutzmassnahmen ───────────────────────────────────────────────────
    {
        key: 'check_14',
        nummer: 'Check 14',
        bereich: 'schutzmassnahmen',
        frage: 'Wurde zuerst das Arbeitsverfahren mit der geringsten Gefährdung gewählt und '
            + 'dann nach TOP festgelegt (Technik vor Organisation vor Person)?',
        warum: 'In der Praxis wird die PSA überproportional priorisiert. Vorrang haben '
            + 'expositionsarme oder -freie Arbeitsverfahren, Atemluftversorgung von '
            + 'Baumaschinen, Bewetterung und Staubminimierung.',
        kapitel: 'Schutzmaßnahmen',
        automatisch: false,
    },
    {
        key: 'check_14b',
        nummer: 'Check 14b',
        bereich: 'schutzmassnahmen',
        frage: 'Sind die Maßnahmen projektspezifisch — oder aus einem früheren Plan '
            + 'übernommen?',
        warum: 'Die Uebernahme aus Vorprojekten ohne Anpassung ist ein benannter Mangel. Was '
            + 'auf einer anderen Baustelle richtig war, ist hier nicht automatisch richtig.',
        kapitel: 'Schutzmaßnahmen',
        automatisch: false,
    },

    // ── PSA ────────────────────────────────────────────────────────────────
    {
        key: 'check_15',
        nummer: 'Check 15',
        bereich: 'psa',
        frage: 'Ist die PSA TAETIGKEITSBEZOGEN und konkret festgelegt (nach TRGS 524 '
            + 'Anlage 8)?',
        warum: 'Pauschale PSA für alle Tätigkeiten führt zu inadäquater oder '
            + 'überdimensionierter Ausrüstung. Unbestimmte Begriffe wie '
            + '„Einwegchemikalienanzug“ oder „geeigneter Filteratemschutz“ sind keine '
            + 'Festlegung, sondern deren Vermeidung.',
        kapitel: 'Schutzklassen',
        automatisch: true,
    },
    {
        key: 'check_16',
        nummer: 'Check 16',
        bereich: 'psa',
        frage: 'Sind Wechselkriterien, Lagerung, Wartung, Gerätewart und der Umgang mit '
            + 'gebrauchter PSA geregelt?',
        warum: 'Die beste PSA-Auswahl nützt nichts, wenn niemand weiß, wann der Filter '
            + 'gewechselt wird und wohin der gebrauchte Anzug kommt.',
        kapitel: 'Schutzklassen',
        automatisch: true,
    },
    {
        key: 'check_17',
        nummer: 'Check 17',
        bereich: 'psa',
        frage: 'Sind Tragezeitbegrenzungen nach DGUV Regel 112-190 Anhang 2 ermittelt — mit '
            + 'Korrekturen für Arbeitsschwere und Klima — und ist die Leistungsminderung in '
            + 'der Bauzeitenplanung berücksichtigt?',
        warum: 'Die Werte des Anhangs 2 gelten für mittlere Arbeitsschwere und etwa '
            + 'Raumtemperatur. Unter Chemikalienschutzkleidung im Sommer gilt beides nicht. '
            + 'Ein Plan, der das ignoriert, wird auf der Baustelle zulasten der Maske gelöst.',
        kapitel: 'Schutzklassen',
        automatisch: true,
    },

    // ── Messung ────────────────────────────────────────────────────────────
    {
        key: 'check_18',
        nummer: 'Check 18',
        bereich: 'messung',
        frage: 'Trägt jede Messung ein benanntes Ziel nach TRGS 524 Anlage 9 (Akutgefahr, '
            + 'chronische Wirkung, Wirksamkeit, Grenzwert)?',
        warum: 'Vage Messangaben ohne klare Zieldefinition erzeugen nur vorgetäuschte '
            + 'Sicherheit. Messtechnik ist kein Ersatz für mangelhaft definierte '
            + 'Schutzmaßnahmen.',
        kapitel: 'Messkonzept',
        automatisch: true,
    },
    {
        key: 'check_19',
        nummer: 'Check 19',
        bereich: 'messung',
        frage: 'Sind Gerät, Kalibrierung, Alarmschwelle und die auszulösende Maßnahme je '
            + 'Parameter festgelegt?',
        warum: 'Ohne festgelegte Reaktion ist ein Messwert nur eine Zahl. Die Eignung der '
            + 'Messenden und die Kalibrierung gehören zur Messung wie das Gerät.',
        kapitel: 'Messkonzept',
        automatisch: true,
    },
    {
        key: 'check_20',
        nummer: 'Check 20',
        bereich: 'messung',
        frage: 'Ist berücksichtigt, dass die Methoden der TRGS 402 bei wechselnden '
            + 'Bedingungen in kontaminierten Bereichen nicht anwendbar sind?',
        warum: 'Eine Arbeitsplatzgrenzwert-Ermittlung nach TRGS 402 setzt vergleichbare '
            + 'Bedingungen voraus. Auf einer Sanierungsbaustelle ändern sie sich täglich.',
        kapitel: 'Messkonzept',
        automatisch: false,
    },
];

const NACH_KEY = new Map(PRUEFLISTE.map(p => [p.key, p]));

export const pruefPunkt = (key: string): PruefPunkt | null => NACH_KEY.get(key) ?? null;

/** Die Punkte eines Bereichs, in Katalogreihenfolge. */
export const punkteDesBereichs = (bereich: PruefPunkt['bereich']): PruefPunkt[] =>
    PRUEFLISTE.filter(p => p.bereich === bereich);

export const BEREICH_LABEL: Record<PruefPunkt['bereich'], string> = {
    erkundung: 'Historische Erkundung',
    analytik: 'Analytik und Probenahme',
    stoffe: 'Stoffeigenschaften',
    arbeitsbereiche: 'Arbeitsbereiche und Tätigkeiten',
    beurteilung: 'Gefährdungsbeurteilung',
    schutzmassnahmen: 'Schutzmaßnahmen',
    psa: 'Persönliche Schutzausrüstung',
    messung: 'Messtechnische Ueberwachung',
};
